'use client';

/**
 * @fileoverview PDP “Add to cart” island — wires `shopify-buy` checkout mutations.
 *
 * **Dynamic pricing** (`isDynamicPricing === true`, e.g. from `ProductPricingIsland`):
 * - Requires a logged-in user; guests cannot add to cart on this path.
 * - Each line item stores only **identity** in `customAttributes`: `_nest_product_id`,
 *   `_nest_brand`, `_nest_product_type`. The server resolves money on `POST /api/draftorder`.
 *
 * **Legacy / removed:** Shopify Function + `POST /api/products/pricing/sign` (HMAC attributes)
 * is commented out below; see `nest-dynamic-pricing` extension if Plus is ever required again.
 *
 * @see docs/SHOPIFY_INTEGRATION.md
 */

import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { CART_TOKEN, GCART_TOKEN, navigateWithHistory, gtmClickTracking } from "@/utils";
import { useAuth } from "@nestingale/auth/hooks";
import { useShopify } from "@/hooks/useShopify";
import { CartInfo, getUserCartInfo, getUserInfo, updateCartData } from "@/utils/useUserInfo";
import { useRouter } from "next/navigation";
import QuantityBox from "@/app/components/quantity";
import cssStyles from '../../[vendor]/product/product.module.scss';

interface AddToCartIslandProps {
    /** Nest catalogue id — stored on the line as `_nest_product_id` when dynamic pricing is on. */
    productId: string;
    productName: string;
    /** Numeric Shopify product id segment from CMS (used to build `gid://shopify/Product/...`). */
    productShopifyId: string;
    /** Display / analytics only on this island — not sent as the authoritative price when dynamic. */
    price: number;
    availabilityCount?: number;
    /**
     * Enables Draft Order checkout metadata on line items (`_nest_*` attributes).
     * When true, user must be signed in to add to cart.
     */
    isDynamicPricing?: boolean;
    /** Nest product type filter — becomes `_nest_product_type` and matches `getProductsData` queries. */
    productType?: string;
    /** Vendor slug (e.g. `gtr`) — becomes `_nest_brand` for server-side catalogue resolution. */
    brand?: string;
}

/**
 * Client island: creates/resumes the Storefront checkout, checks availability, adds line items.
 */
const AddToCartIsland: React.FC<AddToCartIslandProps> = ({
    productId,
    productName,
    productShopifyId,
    price,
    availabilityCount,
    isDynamicPricing = false,
    productType,
    brand,
}) => {
    const router = useRouter();
    const {
        checkoutState,
        createCheckout,
        addVariant,
        getClient,
        checkoutID,
    } = useShopify();
    const { userEmail, isAuthenticated } = useAuth();
    console.log('[AddToCart-ISLAND] userEmail:', userEmail);
    console.log('[AddToCart-ISLAND] isAuthenticated:', isAuthenticated);

    const [qty, setQty] = useState(1);
    const [addToCartClicked, setAddToCartClicked] = useState(false);
    const [checkoutLoaded, setCheckoutLoaded] = useState(false);
    const [availabilityUpdated, setAvailabilityUpdated] = useState(false);
    const [productAvailability, setProductAvailability] = useState('unavailable');
    const [productVariantId, setProductVariantId] = useState<any>(undefined);
    const [userInfo, setUserInfo] = useState<any>(null);

    useEffect(() => {
        async function fetchUserData() {
            const info = await getUserInfo();
            const userInfoWithStatus: any = info ? { ...info, status: "DONE" } : { status: "DONE" };
            console.log('[AddToCart-ISLAND] userInfo resolved:', { status: userInfoWithStatus.status, hasCrtD: !!userInfoWithStatus?.crtD, cId: userInfoWithStatus?.cId, unauthorized: userInfoWithStatus?.unauthorized });
            setUserInfo(userInfoWithStatus);
        }
        fetchUserData();

        const handleAuthLogin = () => {
            console.info('[AddToCart-ISLAND] Auth login detected — re-fetching user info.');
            fetchUserData();
        };

        window.addEventListener('nest:auth:login', handleAuthLogin);
        return () => window.removeEventListener('nest:auth:login', handleAuthLogin);
    }, []);

    useEffect(() => {
        async function loadShopifyCheckout() {
            let checkoutId: any = null;
            console.log('[AddToCart-ISLAND] loadShopifyCheckout gate:', { userStatus: userInfo?.status, isWindow: typeof window !== 'undefined' });
            if (typeof window !== 'undefined' && userInfo?.status === 'DONE') {
                try {
                    checkoutId = userInfo?.crtD?.cartId;
                    if (!checkoutId) {
                        const cartData: CartInfo = await getUserCartInfo(userInfo);
                        checkoutId = cartData.cartId;
                    }
                    //@ts-ignore
                    checkoutId = checkoutId || window.getCookie(CART_TOKEN);
                    checkoutId = (checkoutId === "null" || checkoutId === "undefined") ? null : checkoutId;
                    if (!checkoutId) {
                        //@ts-ignore
                        checkoutId = window.getCookie(GCART_TOKEN);
                        checkoutId = (checkoutId === "null" || checkoutId === "undefined") ? null : checkoutId;
                    }
                    console.log('[AddToCart-ISLAND] calling createCheckout with id:', checkoutId);
                    const result = await createCheckout({ checkoutId });
                    console.log('[AddToCart-ISLAND] createCheckout result:', { checkoutId: result?.id, lineItems: result?.lineItems?.length });
                } catch (error) {
                    console.error('[AddToCart-ISLAND] error creating checkout:', error);
                    setProductAvailability('unavailable');
                }
                setCheckoutLoaded(true);
                console.log('[AddToCart-ISLAND] checkoutLoaded = true');
            }
        }
        loadShopifyCheckout();
    }, [userInfo]);

    useEffect(() => {
        async function updateProductAvail() {
            const shopifyProductId = 'gid://shopify/Product/' + productShopifyId;
            const shopifyClient = getClient();
            console.log('[AddToCart-ISLAND] product.fetch starting:', {
                shopifyProductId,
                hasClient: !!shopifyClient,
                hasProductFetch: !!shopifyClient?.product?.fetch,
                domain: shopifyClient?.config?.domain,
                token: shopifyClient?.config?.storefrontAccessToken ? '***' + shopifyClient.config.storefrontAccessToken.slice(-4) : 'MISSING',
            });
            try {
                const shopifyProd = await shopifyClient.product.fetch(shopifyProductId);
                console.log('[AddToCart-ISLAND] product.fetch result:', { found: !!shopifyProd, variantCount: shopifyProd?.variants?.length });
                if (shopifyProd) {
                    const variant = shopifyProd?.variants[0];
                    if (variant) {
                        setProductVariantId(variant.id);
                        setProductAvailability('available');
                    }
                }
            } catch (error) {
                console.error('[AddToCart-ISLAND] error fetching product from Shopify:', error);
                setProductAvailability('unavailable');
            }
            setAvailabilityUpdated(true);
            console.log('[AddToCart-ISLAND] availabilityUpdated = true, availability:', productAvailability);
        }
        if (checkoutLoaded) {
            updateProductAvail();
        }
    }, [checkoutLoaded]);

    /**
     * Adds the configured variant + qty to the shared Storefront checkout.
     *
     * Dynamic branch stamps `_nest_*` attributes so `ShopifyCart` + `/api/draftorder` can
     * reconstruct Nest identity without trusting browser-supplied prices.
     */
    async function addToCartClick(): Promise<void> {
        if (addToCartClicked) return;
        setAddToCartClicked(true);

        if (checkoutID) {
            // Priority: userInfo state > hook
            const email = userInfo?.email || userEmail;
            const isAuthorized = isAuthenticated;

            if (isDynamicPricing && !isAuthorized) {
                console.warn('[AddToCart-ISLAND] Dynamic pricing requires sign-in', { userInfo, email });
                setAddToCartClicked(false);
                return;
            }

            if (typeof window !== 'undefined') {
                if (isAuthorized) {
                    //@ts-ignore
                    window.setCookieWithoutSecure(CART_TOKEN, checkoutState.id, 10);
                } else if (!isDynamicPricing) {
                    //@ts-ignore
                    window.setCookieWithoutSecure(GCART_TOKEN, checkoutState.id, 10);
                }
            }

            // ── Dynamic pricing (Draft Order checkout): Nest identity on line items ──
            // Prices are resolved server-side at checkout via /api/draftorder — never from the client.
            // [DEPRECATED / REMOVE LATER] Shopify Plus Function + /api/products/pricing/sign path:
            // HMAC-signed _target_price + _price_signature was used with nest-dynamic-pricing extension.
            /*
            if (isDynamicPricing && productVariantId) {
                try {
                    const signRes = await fetch('/showroom/api/products/pricing/sign', { ... });
                    ...
                }
            }
            */

            if (isDynamicPricing && productVariantId) {
                const lineItemsToAdd = [{
                    variantId: productVariantId,
                    quantity: qty,
                    customAttributes: [
                        { key: '_nest_product_id', value: productId },
                        { key: '_nest_brand', value: brand ?? '' },
                        { key: '_nest_product_type', value: productType ?? '' },
                    ],
                }];
                await addVariant(checkoutID, lineItemsToAdd);
                console.log('[AddToCart-ISLAND] Dynamic pricing line item added (Nest draft-order metadata)');
                return;
            }

            // ── Standard (catalog price) path ─────────────────────────────────
            const lineItemsToAdd = [{ variantId: productVariantId, quantity: qty }];
            await addVariant(checkoutID, lineItemsToAdd);
        }
    }

    useEffect(() => {
        if (!addToCartClicked) return;
        async function goToCart() {
            const _lineItemsDebug = checkoutState?.lineItems?.map((item: any) => ({
                title: item.title,
                qty: item.quantity,
                catalogPrice: item.variant?.price?.amount,
                nestProductId: item.customAttributes?.find((a: any) => a.key === '_nest_product_id')?.value,
            }));
            console.log('[AddToCart-ISLAND] → goToCart pre-navigation', {
                userId: userInfo?.cId,
                cartId: checkoutState?.id,
                lineItemCount: checkoutState?.lineItems?.length,
                lineItems: _lineItemsDebug,
                totalPrice: checkoutState?.totalPrice?.amount,
            });
            await updateCartData(userInfo, checkoutState);
            gtmClickTracking({
                event: 'Pdp_add_to_cart_clicked',
                productId,
                ecommerce: {
                    currency: 'USD',
                    value: price * qty,
                    items: [{ item_id: productId, item_name: productName, price, quantity: qty }],
                },
            });
            setTimeout(() => {
                navigateWithHistory(router, `/cart`);
            }, 5000);
        }
        goToCart();
    }, [checkoutState]);

    const handleClose = (value: number) => {
        setQty(value);
    };

    return (
        <Box component="div" sx={{ mt: 2, width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    gap: 1.5,
                    width: '100%',
                }}
            >
                <Box
                    component="div"
                    className={`${cssStyles.option} ${cssStyles["type-qty"]}`}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginRight: '8px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 500,
                        }}
                    >
                        Qty:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <QuantityBox
                            decrement={(_id: any, quantity_num: any, _e: any) => handleClose(quantity_num > 0 ? quantity_num - 1 : quantity_num)}
                            increment={(_id: any, quantity_num: any, _sku: any, _e: any) => handleClose(availabilityCount && quantity_num >= availabilityCount ? availabilityCount : parseInt(quantity_num + 1))}
                            incrementByInput={(_id: any, _sku: any, e: any) => handleClose(availabilityCount && parseInt(e.target.value) > availabilityCount ? availabilityCount : parseInt(e.target.value))}
                            id={""}
                            quantity={qty}
                            variant={undefined}
                            availabilityCount={availabilityCount}
                        />
                    </Box>
                </Box>

                <Box
                    id="shopify-cart"
                    component="div"
                    onClick={(productAvailability?.toLowerCase() !== 'unavailable') ? () => addToCartClick() : undefined}
                    className={cssStyles["add-to-cart"]}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                >
                    {(addToCartClicked || !checkoutLoaded || !availabilityUpdated) ? (
                        <Button
                            disabled
                            color="primary"
                            variant="contained"
                            className={cssStyles["add-to-cart"]}
                            size="large"
                            fullWidth
                            startIcon={<CircularProgress size={20} color="inherit" />}
                            sx={{ height: '40px' }}
                        >
                            {addToCartClicked ? 'Adding to cart..' : 'Loading..'}
                        </Button>
                    ) : (
                        <Button
                            id="pdp_add_to_cart"
                            disabled={productAvailability?.toLowerCase() !== 'available'}
                            variant="contained"
                            color="primary"
                            className={cssStyles["add-to-cart"]}
                            size="large"
                            fullWidth
                            sx={{ height: '40px' }}
                        >
                            {productAvailability?.toLowerCase() !== 'available' ? 'Unavailable' : 'Add to Cart'}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                    color: 'text.secondary',
                }}
            >
                <LocalShippingOutlinedIcon sx={{ fontSize: '24px', mr: 1, color: 'text.primary' }} />
                <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontSize: '14px', lineHeight: 1.5, color: 'text.primary' }}
                >
                    Free Shipping! Arrives in less than 2 weeks
                </Typography>
            </Box>
        </Box>
    );
};

export default AddToCartIsland;
